import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'
import { toast } from 'react-toastify'
import { useMutation } from '@tanstack/react-query'
import { FaCircleCheck } from 'react-icons/fa6'

import { sendResetPasswordEmail } from '@/services/contribution/api.tsx'
import { useSchool } from '@/hooks'
import LoadingScreen from '@/components/LoadingScreen'
import SchoolLogo from '@/components/SchoolLogo'
import Section from '@components/Section'
import Button from '@components/Button'
import Input from '@components/Input'

export default function ForgotPasswordPage() {
  const { t } = useTranslation()
  const { schoolSlug } = useParams()
  const navigate = useNavigate()

  const { data, isLoading } = useSchool(schoolSlug)

  const [email, setEmail] = useState('')
  const [showConfirmation, setShowConfirmation] = useState(false)

  const resetPasswordMutation = useMutation({
    mutationFn: (email: string) => sendResetPasswordEmail(schoolSlug!, email),
    onSuccess: (success) => {
      if (success) {
        toast.success(t('content.components.forgot_password.success_toast'))
        setShowConfirmation(true)
      } else {
        toast.error(t('content.components.forgot_password.error_toast'), { autoClose: false })
      }
    },
    onError: (error) => {
      if (error instanceof Error) {
        Sentry.captureException(error, {
          tags: { location: 'Forgot Password' },
          extra: { schoolSlug, email, errorMessage: error.message },
        })
      }
      toast.error(t('content.components.forgot_password.error_toast'), { autoClose: false })
    },
  })

  function handleSubmit() {
    if (!email || !schoolSlug) return
    resetPasswordMutation.mutate(email)
  }

  if (isLoading) {
    return <LoadingScreen />
  }

  if (!schoolSlug) {
    navigate('/')
    return null
  }

  const schoolLogo = data?.schoolLogo || undefined
  const schoolName = data?.schoolName || ''

  return (
    <>
      <title>Forgot Password - Book Blast by Books Are Fun</title>
      <div className='w-full lg:w-3/4 mx-auto'>
        <div className='bg-cta text-center px-4 md:px-8'>
          <h1 className='text-white text-3xl font-serif text-center font-bold'>
            {t('content.components.forgot_password.title')}
          </h1>
          <div className='text-white pt-2'>{t('content.components.forgot_password.subtitle')}</div>
        </div>

        <Section wrapperStyle='my-8 justify-center items-center gap-4'>
          <SchoolLogo url={schoolLogo} />
          <div className='w-full text-center font-serif text-xl font-bold mt-4 mb-8'>{schoolName}</div>

          {showConfirmation ? (
            <div className='flex flex-col gap-4 justify-center items-center h-full text-center'>
              <div className='flex flex-row items-center gap-2'>
                <FaCircleCheck className='text-leaf-green h-6 w-6' />
                <h2 className='text-2xl font-serif font-semibold text-leaf-green'>
                  {t('content.components.forgot_password.success_title')}
                </h2>
              </div>
              <p
                className='text-gray-700'
                dangerouslySetInnerHTML={{ __html: t('content.components.forgot_password.success_subtitle') }}
              />
            </div>
          ) : (
            <div className='flex flex-col gap-4 justify-center items-center h-full w-full'>
              <div className='w-full'>
                <Input
                  type='email'
                  label={t('content.components.input_contact.email_field_title')}
                  value={email}
                  onInput={setEmail}
                  placeholder={t('content.components.input_contact.email_field_placeholder')}
                />
              </div>
              <Button
                variant='cta'
                label={t('content.components.forgot_password.button_title')}
                fullwidth
                onClick={handleSubmit}
                disabled={resetPasswordMutation.isPending}
              />
            </div>
          )}
        </Section>
      </div>
    </>
  )
}
